import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import compact from 'lodash/compact';
import { toast } from 'react-toastify';
import classes from './mdoModal.module.scss';
import api from '../../../../../api/api';
import utils from '../../../../../utils/utils';
import accountHelpers from '../../../../../utils/accountHelpers';
import Modal from '../../../../_common/Modal/Modal';
import Checkbox from '../../../../_common/Form/Checkbox/Checkbox';
import InputField from '../../../../_common/Form/TextFields/InputField';
import Button from '../../../../_common/Form/Buttons/Button';
import Loader from '../../../../_common/Loader/Loader';
import SortpakLogo from '../../../../../assets/icons/sortpak-logo-fax.png';

const MDOModal = ({
  isOpen, onClose, info, changeTabCounts,
}) => {
  const [medications, setMedications] = useState([]);
  const [comment, setComment] = useState('');
  const [data, setData] = useState({
    emailChecked: !!info.Physician?.email,
    faxChecked: !!info.Physician?.fax,
    email: info.Physician?.email || '',
    fax: info.Physician?.fax || '',
    emailError: false,
    faxError: false,
  });
  const [loading, loadingToggle] = useState(true);
  const [sendLoading, sendLoadingToggle] = useState(false);

  useEffect(() => {
    if (isOpen) {
      (async () => {
        loadingToggle(true);

        const { data: { response } } = await api.getScripts({
          status: 'Prior Auth',
          limit: 100,
          userId: accountHelpers.getUser().id,
          patientId: info.PatientId,
          physicianId: info.PhysicianId,
        });

        setMedications(response.map((script) => ({
          scriptId: script.id,
          checked: true,
          name: script.Product?.name || '',
          quantity: script.quantity || '',
          daysSupply: script.daysSupply || '',
          refillsRemaining: script.refillsRemaining || '',
          dxCode: '',
          coverMyMedsKey: '',
        })));

        loadingToggle(false);
      })();
    }
  }, [isOpen]);

  const patientCityStateZip = compact([
    info.Patient?.addressStreet,
    info.Patient?.addressCity,
    info.Patient?.addressState,
    info.Patient?.addressZipCode,
  ]).join(', ');

  const physicianCityStateZip = compact([
    info.Physician?.addressStreet,
    info.Physician?.addressCity,
    info.Physician?.addressState,
    info.Physician?.addressZipCode,
  ]).join(', ');

  const updateMedication = (index, field, value) => {
    setMedications((prev) => prev.map((med, i) => (i === index ? { ...med, [field]: value } : med)));
  };

  const changeData = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value, [`${field}Error`]: '' }));
  };

  const send = async () => {
    const {
      email, emailChecked, fax, faxChecked,
    } = data;

    setData((prev) => ({ ...prev, emailError: false, faxError: false }));

    if (!emailChecked && !faxChecked) {
      toast.error('Please select Email or Fax');
      return;
    }

    const errors = {};

    if ((emailChecked && !email.trim()) && (faxChecked && !fax.trim())) {
      toast.error('Please enter Email and Fax');

      errors.emailError = true;
      errors.faxError = true;
    } else if (emailChecked && !email.trim()) {
      toast.error('Please enter Email');

      errors.emailError = true;
    } else if (faxChecked && !fax.trim()) {
      toast.error('Please enter Fax');

      errors.faxError = true;
    } else if (emailChecked && email.trim() && !utils.isEmail(email.trim())) {
      toast.error('Please enter a valid Email');

      errors.emailError = true;
    }

    if (Object.keys(errors).length) {
      setData((prev) => ({ ...prev, ...errors }));

      return;
    }

    const sendingData = {
      scriptId: info.id,
      medications: medications
        .filter((med) => med.checked)
        .map((med) => ({ scriptId: med.scriptId, dxCode: med.dxCode, coverMyMedsKey: med.coverMyMedsKey })),
      comment,
    };

    if (emailChecked && email.trim()) {
      sendingData.email = email.trim();
    }

    if (faxChecked && fax.trim()) {
      sendingData.fax = fax.trim();
    }

    try {
      sendLoadingToggle(true);
      await api.communicateMDO(sendingData);

      toast.success('Sent successfully');
      onClose();

      changeTabCounts('Faxes', 1);
    } catch (e) {
      console.warn(e);

      toast.error(e.response?.data?.message || 'Something went wrong');
    }

    sendLoadingToggle(false);
  };

  return (
    <Modal onClose={onClose} open={isOpen} className={classes.modal_wrapper}>
      <div className={classes.document}>
        {loading
          ? <Loader />

          : (
            <>
              <div style={{
                background: '#fff', margin: '0 auto', padding: '10px 15px', width: '850px', boxSizing: 'border-box',
              }}
              >
                <div style={{ overflow: 'hidden', marginBottom: '10px' }}>
                  <div style={{ float: 'left' }}>
                    <img
                      alt="SortPak"
                      src={SortpakLogo}
                      style={{ height: '55px', display: 'block', marginBottom: '6px' }}
                    />

                    <div><strong>Prior Authorization Request</strong></div>

                    <div>{`Sent on ${moment().format('MM/DD/YYYY')}`}</div>
                  </div>

                  <div style={{
                    float: 'right', textAlign: 'right', fontWeight: 'bold', fontSize: '11px',
                  }}
                  >
                    <div>
                      655 N Central Avenue, 22
                      <sup>nd</sup>
                      {' '}
                      Floor, Glendale, CA, 91203
                    </div>

                    <div>TEL: (877) 570-7787</div>

                    <div>FAX: (877) 475-2382</div>
                  </div>

                  <div style={{ clear: 'both' }} />
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid #000', margin: '0 0 12px 0' }} />

                <div style={{ overflow: 'hidden', marginBottom: '12px' }}>
                  <div style={{
                    float: 'left', width: '25%', paddingRight: '8px', boxSizing: 'border-box',
                  }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Patient name</div>

                    <div>{`${info.Patient?.firstName || ''} ${info.Patient?.lastName || ''}`}</div>
                  </div>

                  <div style={{
                    float: 'left', width: '18%', paddingRight: '8px', boxSizing: 'border-box',
                  }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Date of birth</div>

                    <div>{info.Patient?.dob ? moment(info.Patient.dob).format('MM/DD/YYYY') : '-'}</div>
                  </div>

                  <div style={{
                    float: 'left', width: '18%', paddingRight: '8px', boxSizing: 'border-box',
                  }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Phone</div>

                    <div>{info.Patient?.phone || '-'}</div>
                  </div>

                  <div style={{ float: 'left', width: '39%', boxSizing: 'border-box' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Address</div>

                    <div>
                      {patientCityStateZip}
                    </div>
                  </div>

                  <div style={{ clear: 'both' }} />
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Patient Insurance Info</div>

                  <div>{`Plan: ${info.Prim?.plan || ''}`}</div>

                  <div>{`Member ID: ${info.Prim?.insID || ''}`}</div>

                  <div>{`RxBIN: ${info.Prim?.BIN || ''}`}</div>

                  <div>{`RxPCN: ${info.Prim?.PCN || ''}`}</div>

                  <div>{`Group: ${info.Prim?.insGroup || ''}`}</div>
                </div>

                <div style={{ overflow: 'hidden', marginBottom: '16px' }}>
                  <div style={{
                    float: 'left', width: '28%', paddingRight: '8px', boxSizing: 'border-box',
                  }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Physician name</div>

                    <div>{`${info.Physician?.firstName || ''} ${info.Physician?.lastName || ''}`}</div>
                  </div>

                  <div style={{
                    float: 'left', width: '18%', paddingRight: '8px', boxSizing: 'border-box',
                  }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Phone</div>

                    <div>{info.Physician?.phone || '-'}</div>
                  </div>

                  <div style={{
                    float: 'left', width: '18%', paddingRight: '8px', boxSizing: 'border-box',
                  }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Fax</div>

                    <div>{info.Physician?.fax || '-'}</div>
                  </div>

                  <div style={{ float: 'left', width: '36%', boxSizing: 'border-box' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Address</div>

                    <div>
                      {physicianCityStateZip}
                    </div>
                  </div>

                  <div style={{ clear: 'both' }} />
                </div>

                <div style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '8px' }}>
                  The following medications Require Prior Authorizations
                </div>

                <table style={{
                  width: '100%', borderCollapse: 'collapse', marginBottom: '14px', fontSize: '12px',
                }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{ padding: '6px 8px', border: '1px solid #ccc', width: '36px' }}
                        aria-label="Select"
                      />

                      <th
                        style={{ padding: '6px 8px', border: '1px solid #ccc', textAlign: 'left' }}
                        aria-label="Medication"
                      />

                      <th style={{
                        padding: '6px 8px', border: '1px solid #ccc', textAlign: 'center', width: '65px',
                      }}
                      >
                        Quantity
                      </th>

                      <th style={{
                        padding: '6px 8px', border: '1px solid #ccc', textAlign: 'center', width: '75px',
                      }}
                      >
                        Days Supply
                      </th>

                      <th style={{
                        padding: '6px 8px', border: '1px solid #ccc', textAlign: 'center', width: '75px',
                      }}
                      >
                        Refills Remaining
                      </th>

                      <th style={{
                        padding: '6px 8px', border: '1px solid #ccc', textAlign: 'center', width: '160px',
                      }}
                      >
                        DX Code
                      </th>

                      <th style={{
                        padding: '6px 8px', border: '1px solid #ccc', textAlign: 'center', width: '204px',
                      }}
                      >
                        The CoverMyMeds Request Key
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {medications.map((med, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                      <tr key={i}>
                        <td style={{
                          padding: '8px 6px', border: '1px solid #ccc', textAlign: 'center', width: '36px',
                        }}
                        >
                          <Checkbox
                            checked={med.checked}
                            onChange={(v) => updateMedication(i, 'checked', v)}
                          />
                        </td>

                        <td style={{ padding: '8px 6px', border: '1px solid #ccc' }}>
                          {med.name || `Medication name ${i + 1}`}
                        </td>

                        <td style={{
                          padding: '8px 6px', border: '1px solid #ccc', textAlign: 'center', width: '65px',
                        }}
                        >
                          {med.quantity}
                        </td>

                        <td style={{
                          padding: '8px 6px', border: '1px solid #ccc', textAlign: 'center', width: '75px',
                        }}
                        >
                          {med.daysSupply}
                        </td>

                        <td style={{
                          padding: '8px 6px', border: '1px solid #ccc', textAlign: 'center', width: '75px',
                        }}
                        >
                          {med.refillsRemaining}
                        </td>

                        <td style={{ padding: '4px 6px', border: '1px solid #ccc', width: '160px' }}>
                          <InputField
                            wrapperClassName={classes.input}
                            value={med.dxCode}
                            onChange={(v) => updateMedication(i, 'dxCode', v)}
                            onKeyPress={(e) => utils.typingMaxLength(e, 10)}
                            onPaste={(e) => utils.typingMaxLength(e, 10, true)}
                            height={32}
                            disabled={!med.checked}
                          />
                        </td>

                        <td style={{ padding: '4px 6px', border: '1px solid #ccc', width: '204px' }}>
                          <InputField
                            wrapperClassName={classes.input}
                            value={med.coverMyMedsKey}
                            onChange={(v) => updateMedication(i, 'coverMyMedsKey', v)}
                            onKeyPress={(e) => utils.typingMaxLength(e, 10)}
                            onPaste={(e) => utils.typingMaxLength(e, 10, true)}
                            height={32}
                            disabled={!med.checked}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={{ border: '1px solid #ccc', padding: '8px', marginBottom: '14px' }}>
                  <p style={{ margin: '0 0 6px', fontWeight: 'bold' }}>Comment Box:</p>

                  <InputField
                    value={comment}
                    onChange={setComment}
                    textareaField
                  />
                </div>

                <div style={{ fontSize: '10px' }}>
                  <div style={{ fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Confidentiality Notice
                  </div>

                  <p style={{ margin: 0 }}>
                    The information contained in this transmittal belongs to SortPak Pharmacy and may include
                    information that is confidential, privileged, and protected from disclosure under applicable
                    law. It is intended only for the use of the above physicians. If you are not the intended
                    recipient of this information, you are hereby notified that any disclosure, copying, or
                    distribution of this information is strictly prohibited. If you have received this transmittal
                    in error, please immediately notify us by telephone at (877) 570-7787. Thank you. Please utilize
                    electronic prescribing when possible.
                  </p>
                </div>

              </div>

              <div className={classes.actions}>
                <div className={classes.mode_options}>
                  <div className={classes.mode_row}>
                    <Checkbox
                      checked={data.emailChecked}
                      onChange={(v) => {
                        setData((prev) => ({ ...prev, emailChecked: v, emailError: false }));
                      }}
                    />

                    <InputField
                      label="Email"
                      value={data.email}
                      onChange={(v) => changeData('email', v)}
                      error={data.emailError}
                      height={32}
                      labelDefaultPosition={4}
                    />
                  </div>

                  <div className={classes.mode_row}>
                    <Checkbox
                      checked={data.faxChecked}
                      onChange={(v) => {
                        setData((prev) => ({ ...prev, faxChecked: v, faxError: false }));
                      }}
                    />

                    <InputField
                      label="Fax"
                      value={data.fax}
                      onChange={(v) => changeData('fax', v)}
                      error={data.faxError}
                      height={32}
                      labelDefaultPosition={4}
                    />
                  </div>
                </div>

                <div className={classes.buttons}>
                  <Button
                    title="Close"
                    clearButton
                    onClick={onClose}
                  />

                  <Button
                    title="Send"
                    reverseColor
                    onClick={send}
                    disabled={!medications.length || medications.every((m) => !m.checked)}
                    loading={sendLoading}
                  />
                </div>
              </div>
            </>
          )}
      </div>
    </Modal>
  );
};

MDOModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  info: PropTypes.object.isRequired,
};

export default MDOModal;
